import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { faGear, faTrashCan, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { map } from 'rxjs';

import { MediaService } from '@eiq/files';
import { Media, MediaView } from '@eiq/files/models';

import { NotificationLevel, NotificationsService } from '@eiq/notifications';
import { ImageSettingTypeEnum, SystemService } from '@eiq/shared/data';
import { ConfirmDialogComponent } from '@eiq/ui';

import { FileType } from '@empathy/global/data';
import { CustomTerm } from '@empathy/survey/base';

import { ISettingsForm } from '../../models/survey-editor/settings-form.model';

@Component({
  selector: 'empathy-settings-form',
  templateUrl: 'settings-form.component.html',
  styleUrls: ['./settings-form.component.scss']
})
export class SettingsFormComponent {
  settingsForm = input.required<FormGroup<ISettingsForm>>();

  defaultLogo$ = computed<Media | null>(() => {
    const settings = this.tenantSettings$();
    const defaultLogoSetting = settings?.imageSettings?.find((imageSetting) => imageSetting.type === ImageSettingTypeEnum.ResearchLargeLogo);
    return this.mapImageSettingToMedia(defaultLogoSetting);
  });

  defaultIconLogo$ = computed<Media | null>(() => {
    const settings = this.tenantSettings$();
    const defaultIconLogoSetting = settings?.imageSettings?.find((x) => x.type === ImageSettingTypeEnum.ResearchSmallLogo);
    return this.mapImageSettingToMedia(defaultIconLogoSetting);
  });

  usingDefaultLogo$ = computed(() => {
    const currentId = this.settingsForm()?.get('logo')?.get('mediaId')?.value;
    const defaultId = this.defaultLogo$()?.mediaId;
    return !!defaultId && !!currentId && currentId === defaultId;
  });

  usingDefaultIconLogo$ = computed(() => {
    const currentId = this.settingsForm()?.get('iconLogo')?.get('mediaId')?.value;
    const defaultId = this.defaultIconLogo$()?.mediaId;
    return !!defaultId && !!currentId && currentId === defaultId;
  });

  hasMedia$ = computed(() => !!this.logo$()?.id);
  hasIconMedia$ = computed(() => !!this.iconLogo$()?.id);

  logo$ = signal<MediaView>(null);
  iconLogo$ = signal<MediaView>(null);
  uploading$ = signal(false);
  uploadingIcon$ = signal(false);

  faInfoCircle = faInfoCircle;
  faGear = faGear;
  faTrashCan = faTrashCan;

  private tenantSettings$ = toSignal(inject(SystemService).getTenantSystemSettings());

  private dialog = inject(MatDialog);
  private mediaService = inject(MediaService);
  private notificationService = inject(NotificationsService);

  constructor() {
    effect(
      () => {
        const logo = this.settingsForm().get('logo')?.value as Media | null;
        const icon = this.settingsForm().get('iconLogo')?.value as Media | null;

        this.mapLogoView(logo);
        this.mapIconLogoView(icon);
      },
      { allowSignalWrites: true }
    );
  }

  onUploadLogo(file: File): void {
    const fileData = new FormData();
    fileData.append('mediaFile', file);
    fileData.append('itemReference', 'settings__logo');

    this.uploading$.set(true);
    this.mediaService.uploadStimulus(fileData).subscribe((mediaEvent: HttpEvent<{ id: string }>) => {
      if (!mediaEvent) {
        this.uploading$.set(false);
        return;
      }

      if ((mediaEvent as any).body) {
        const media = { mediaId: (mediaEvent as any).body.id, name: file.name, type: file.type };
        this.settingsForm().get('logo').setValue(media);
        this.mapLogoView(media);
      }

      if (mediaEvent.type === HttpEventType.Response) {
        this.uploading$.set(false);
      }
    });
  }

  onUploadIconLogo(file: File): void {
    const fileData = new FormData();
    fileData.append('mediaFile', file);
    fileData.append('itemReference', 'settings__icon-logo');

    this.uploadingIcon$.set(true);
    this.mediaService.uploadStimulus(fileData).subscribe((mediaEvent: HttpEvent<{ id: string }>) => {
      if (!mediaEvent) {
        this.uploadingIcon$.set(false);
        return;
      }

      if ((mediaEvent as any).body) {
        const media = { mediaId: (mediaEvent as any).body.id, name: file.name, type: file.type };
        this.settingsForm().get('iconLogo').setValue(media);
        this.mapIconLogoView(media);
      }

      if (mediaEvent.type === HttpEventType.Response) {
        this.uploadingIcon$.set(false);
      }
    });
  }

  onDeleteLogo(): void {
    const confirmationRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Logo',
        confirmText: 'Are you sure you want to remove it?',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    confirmationRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.settingsForm().get('logo').reset();
        this.logo$.set(null);
      }
    });
  }

  onDeleteIconLogo(): void {
    const confirmationRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Icon Logo',
        confirmText: 'Are you sure you want to remove it?',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    confirmationRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.settingsForm().get('iconLogo').reset();
        this.iconLogo$.set(null);
      }
    });
  }

  onSetDefaultLogo(toggle: MatSlideToggleChange): void {
    if (toggle.checked) {
      this.settingsForm().get('logo').setValue(this.defaultLogo$());
      this.settingsForm().get('logo').updateValueAndValidity();
      this.mapLogoView(this.defaultLogo$());
    } else {
      this.settingsForm().get('logo').reset();
      this.logo$.set(null);
    }
  }

  onSetDefaultIconLogo(toggle: MatSlideToggleChange): void {
    if (toggle.checked) {
      this.settingsForm().get('iconLogo').setValue(this.defaultIconLogo$());
      this.settingsForm().get('iconLogo').updateValueAndValidity();
      this.mapIconLogoView(this.defaultIconLogo$());
    } else {
      this.settingsForm().get('iconLogo').reset();
      this.iconLogo$.set(null);
    }
  }

  onCsvBlocksImport(csvContent: CustomTerm[]): void {
    this.termsCsvImport('blockedTerms', csvContent);
  }

  onCsvFlagsImport(csvContent: CustomTerm[]): void {
    this.termsCsvImport('flaggedTerms', csvContent);
  }

  private termsCsvImport(field: string, csvContent: CustomTerm[]): void {
    let addedTerms = 0;

    if (csvContent?.length) {
      const flaggedTermsArray = this.settingsForm().get('monitorTerms').get(field) as FormArray;

      csvContent.forEach((term) => {
        const isDuplicate = flaggedTermsArray.controls.some((control) => control.get('type')?.value === term.type && control.get('term')?.value === term.term);

        if (!isDuplicate) {
          flaggedTermsArray.push(
            new FormGroup({
              type: new FormControl(term.type),
              term: new FormControl(term.term)
            })
          );
          addedTerms++;
        }
      });

      const firstElementExists =
        (this.settingsForm().get('monitorTerms').get(field) as FormArray).controls.length &&
        (this.settingsForm().get('monitorTerms').get(field) as FormArray).at(0);

      const firstElementIsBlank = (this.settingsForm().get('monitorTerms').get(field) as FormArray).at(0).value.term === '';

      if (firstElementExists && firstElementIsBlank) {
        (this.settingsForm().get('monitorTerms').get(field) as FormArray).removeAt(0);
      }

      this.notificationService.open({
        level: NotificationLevel.Success,
        title: 'New terms added',
        message: `${addedTerms} terms were successfully added to the list.`
      });
    }

    if (csvContent && !addedTerms) {
      this.notificationService.open({
        level: NotificationLevel.Info,
        title: 'No terms added',
        message: 'No terms were added to the list. The CSV file might be empty or all the terms were already present.'
      });
    }
  }

  private mapLogoView(media: Media | null): void {
    if (!media?.mediaId) {
      this.logo$.set(null);
      return;
    }

    this.logo$.set({
      url$: this.mediaService.getMediaUrl(media.mediaId).pipe(map((mediaResponse) => mediaResponse.url)),
      id: media.mediaId,
      type: media.type as FileType,
      name: media.name
    });
  }

  private mapIconLogoView(media: Media | null): void {
    if (!media?.mediaId) {
      this.iconLogo$.set(null);
      return;
    }

    this.iconLogo$.set({
      url$: this.mediaService.getMediaUrl(media.mediaId).pipe(map((mediaResponse) => mediaResponse.url)),
      id: media.mediaId,
      type: media.type as FileType,
      name: media.name
    });
  }

  private mapImageSettingToMedia(setting?: { mediaId: string; mediaName: string; mediaType: string } | null): Media | null {
    if (!setting?.mediaId) {
      return null;
    }

    return {
      mediaId: setting.mediaId,
      name: setting.mediaName,
      type: setting.mediaType
    };
  }
}
